import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { MenuItem } from 'schema-dts'

import {
  MenuItemFromCartQuery,
  Menus_VisibilityEnum,
  useDoMenuItemQuery,
  useMenuItemDetailsQuery,
  useMenuItemFromCartLazyQuery,
  useReorderableMenuItemLazyQuery
} from 'src/apollo/onlineOrdering'
import { reportError } from 'src/lib/js/clientError'
import { I18nType } from 'src/lib/js/hooks/useFormatPrice'
import { formatImageURL } from 'src/lib/js/utilities'
import SchemaThing from 'src/public/components/seo/SchemaThing'
import {
  AlertModalSource,
  useAlertModalContext
} from 'shared/components/common/alert_modal/AlertModal'
import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'shared/components/common/modal'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { REQUEST_FAILURE_MESSAGE } from 'shared/js/constants'
import { useItemTags } from 'public/components/default_template/online_ordering/item_tags/ItemTags'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import {
  ModifierContextProvider,
  useModifierContext
} from 'public/components/online_ordering/ModifierContext'
import { SitesToastContainer } from 'src/shared/js/alertUtils'
import {
  Action,
  ModifierAllergenMap
} from 'public/components/online_ordering/types'
import {
  ItemModalRootContent,
  BodyProps,
  headerId
} from 'public/components/default_template/online_ordering/item_modal/ItemModalRootContent'
import { ItemModalSecondaryContent } from 'public/components/default_template/online_ordering/item_modal/ItemModalSecondaryContent'
import { AllergenSheetProvider } from 'public/components/default_template/online_ordering/item_modal/AllergenSheetContext'
import { AllergenInfoSheet } from 'public/components/default_template/online_ordering/item_modal/AllergenInfoSheet'
import { useAllergensWithSource } from 'public/components/default_template/online_ordering/item_modal/useAllergensWithSource'
import {
  collapseModifier,
  collapseModifierGroups
} from 'public/components/online_ordering/modifierUtils'
import { Channel } from 'src/public/js/siteUtilities'

type ItemModalProps = {
  onClose: () => void
  isOpen: boolean
  /** If present with `selectionGuid` from BodyProps, will populate the modal
   to facilitate reordering that item from a past order. */
  orderGuid?: string
  withTransitions?: boolean
  onSuccessfulAddToCart?: () => void
} & BodyProps

const WrappedModalBody = (props: BodyProps, ref: RefObject<HTMLDivElement>) => {
  const { image, name, basePrice, itemGuid, description, allergens } = props
  const { displayedModifier } = useModifierContext()
  const { ooRestaurant } = useRestaurant()
  const [allergenSheetOpen, setAllergenSheetOpen] = useState(false)
  const openAllergenSheet = useCallback(() => setAllergenSheetOpen(true), [])
  const allergensWithSource = useAllergensWithSource(allergens)

  const restaurantI18n: I18nType = ooRestaurant?.i18n as I18nType

  const schema = useMemo(() => {
    if (name) {
      return (
        <SchemaThing<MenuItem>
          json={(routes) => ({
            '@context': 'https://schema.org',
            '@type': 'MenuItem',
            '@id': routes.location,
            name: name,
            ...(description ? { description: description } : {}),
            image: image ? formatImageURL(image) : undefined,
            ...(ooRestaurant?.guid
              ? {
                  subjectOf: {
                    '@type': 'Menu',
                    '@id': routes.orderUrl(ooRestaurant.guid)
                  }
                }
              : {}),
            identifier: itemGuid ?? undefined,
            offers: basePrice
              ? {
                  '@type': 'OfferForPurchase',
                  priceCurrency: restaurantI18n.currency,
                  price: basePrice
                }
              : undefined
          })}
        />
      )
    }
    return null
  }, [
    name,
    description,
    image,
    itemGuid,
    restaurantI18n,
    ooRestaurant?.guid,
    basePrice
  ])

  return (
    <AllergenSheetProvider value={openAllergenSheet}>
      {schema}
      <ItemModalRootContent
        ref={ref}
        {...props}
        allergenSheetOpen={allergenSheetOpen}
        allergensWithSource={allergensWithSource}
      />
      {!displayedModifier?.isRoot && <ItemModalSecondaryContent />}
      <AllergenInfoSheet
        isOpen={allergenSheetOpen}
        onClose={() => setAllergenSheetOpen(false)}
        allergens={allergensWithSource}
      />
    </AllergenSheetProvider>
  )
}

const ModalBody = React.forwardRef(WrappedModalBody)

const WrappedItemModal = (props: ItemModalProps) => {
  const ooClient = useOOClient()
  const rootRef = useRef<HTMLDivElement>(null)
  const { loadingCart: loadingCartState, cartGuid, channel } = useCart()
  const { fulfillmentData } = useFulfillment()
  const { ooRestaurant } = useRestaurant()
  const { isOpen, itemGuid, itemGroupGuid } = props
  const restaurantGuid = ooRestaurant?.guid
  const [loading, setLoading] = useState(false)

  const { openAlertModal } = useAlertModalContext()

  const shouldSkip =
    !ooRestaurant?.guid || !itemGuid || !itemGroupGuid || !isOpen
  const hasCartSelection = Boolean(cartGuid && props.selectionGuid)
  const hasReorderSelection = Boolean(props.orderGuid && props.selectionGuid)
  const hasSelection = hasCartSelection || hasReorderSelection

  const {
    data,
    error,
    loading: loadingDetails,
    called: calledDetails
  } = useMenuItemDetailsQuery({
    variables: {
      input: {
        itemGuid: itemGuid || '',
        itemGroupGuid: itemGroupGuid || '',
        restaurantGuid: restaurantGuid || '',
        dateTime: fulfillmentData?.cartFulfillmentData.fulfillmentDateTime,
        channelGuid: props.channelGuid,
        includeSlugs: channel === Channel.ECOMM
      },
      nestingLevel: 10
    },
    client: ooClient,
    skip: shouldSkip || !isOpen || !!hasSelection
  })

  const [
    loadCartSelection,
    {
      data: cartSelection,
      error: errorCart,
      loading: loadingCart,
      called: calledCart
    }
  ] = useMenuItemFromCartLazyQuery({
    variables: {
      input: {
        itemGuid: itemGuid || '',
        itemGroupGuid: itemGroupGuid || '',
        restaurantGuid: restaurantGuid || '',
        selectionGuid: props.selectionGuid || '',
        cartGuid: cartGuid || '',
        dateTime: fulfillmentData?.cartFulfillmentData?.fulfillmentDateTime,
        channelGuid: props.channelGuid
      },
      nestingLevel: 10
    },
    client: ooClient,
    fetchPolicy: 'no-cache'
  })

  const [
    loadReorderSelection,
    {
      data: reorderSelection,
      error: errorReorder,
      loading: loadingReorder,
      called: calledReorder
    }
  ] = useReorderableMenuItemLazyQuery({
    variables: {
      input: {
        restaurantGuid: restaurantGuid || '',
        orderGuid: props.orderGuid || '',
        selectionGuid: props.selectionGuid || '',
        dateTime: fulfillmentData?.cartFulfillmentData?.fulfillmentDateTime
      },
      nestingLevel: 10
    },
    client: ooClient,
    fetchPolicy: 'no-cache'
  })

  const { data: doMenuItemData, error: errorMenuItem } = useDoMenuItemQuery({
    variables: {
      input: {
        menuGroupGuid: itemGroupGuid || '',
        menuItemGuid: itemGuid || '',
        restaurantGuid: restaurantGuid || '',
        // eslint-disable-next-line camelcase
        visibility: Menus_VisibilityEnum.ToastOnlineOrdering,
        dateTime: fulfillmentData?.cartFulfillmentData.fulfillmentDateTime,
        channelGuid: props.channelGuid
      }
    },
    client: ooClient,
    skip: shouldSkip || !isOpen
  })

  const errorSelection = errorCart || errorReorder
  const loadingSelection = loadingCart || loadingReorder
  const calledSelection = calledCart || calledReorder
  const selectionItemDetails =
    cartSelection?.selectionItemDetails ||
    reorderSelection?.reorderableSelectionItemDetails

  useEffect(() => {
    if (error || errorSelection || errorMenuItem) {
      openAlertModal(
        REQUEST_FAILURE_MESSAGE,
        undefined,
        AlertModalSource.ITEM_MODAL
      )
      reportError(
        'Error loading menu item.',
        error || errorSelection || errorMenuItem
      )
    }
  }, [error, errorSelection, errorMenuItem, openAlertModal])

  const modifierMap =
    doMenuItemData?.doMenus_findMenuItem?.__typename ===
    'DoMenus_FindMenuItemResponse'
      ? doMenuItemData?.doMenus_findMenuItem?.menuResponse?.modifierGroupReferences?.reduce(
          (map, ref) => ({
            ...map,
            [ref.guid]: ref
          }),
          {}
        )
      : {}

  const modifierAllergenMap = useMemo((): ModifierAllergenMap => {
    if (
      doMenuItemData?.doMenus_findMenuItem?.__typename !==
      'DoMenus_FindMenuItemResponse'
    ) {
      return {}
    }

    const refs =
      doMenuItemData.doMenus_findMenuItem.menuResponse?.modifierOptionReferences
    if (!refs) return {}

    return refs.reduce<ModifierAllergenMap>((map, ref) => {
      map[ref.guid] = { name: ref.name, allergens: ref.allergens }
      return map
    }, {})
  }, [doMenuItemData])

  useEffect(() => {
    setLoading(
      (calledDetails && loadingDetails) || (calledSelection && loadingSelection)
    )
  }, [calledDetails, loadingDetails, calledSelection, loadingSelection])

  useEffect(() => {
    if (!shouldSkip && isOpen) {
      if (hasReorderSelection) {
        loadReorderSelection()
      } else if (hasCartSelection) {
        loadCartSelection()
      }
    }
  }, [
    shouldSkip,
    isOpen,
    hasCartSelection,
    hasReorderSelection,
    loadCartSelection,
    loadReorderSelection
  ])

  const modifierGroups = useMemo(
    () =>
      data?.menuItemDetails.modifierGroups
        ? collapseModifierGroups(data?.menuItemDetails.modifierGroups)
        : {},
    [data]
  )

  const rootModifier = useMemo(() => {
    const getModifiersFromSelectionQuery = (
      selectionItemDetails: MenuItemFromCartQuery['selectionItemDetails']
    ) => {
      return {
        ...collapseModifier({
          ...selectionItemDetails,
          price: selectionItemDetails.menuItemPrice
        }),
        specialInstructions: selectionItemDetails?.specialRequest,
        usesFractionalQuantity: selectionItemDetails?.usesFractionalQuantity,
        unitOfMeasure: selectionItemDetails?.fractionalQuantity?.unitOfMeasure,
        name: data?.menuItemDetails.name
      }
    }

    const getModifiersFromMenuQuery = () => {
      return {
        groupGuid: itemGroupGuid || 'root',
        modifier: {
          itemGuid: props.itemGuid!,
          itemGroupGuid,
          modifierGroups: data?.menuItemDetails.modifierGroups || [],
          price: data?.menuItemDetails.prices[0] || 0
        },
        modifierGroups,
        isRoot: true,
        masterId: data?.menuItemDetails?.masterId,
        action: 'replace' as Action,
        price: data?.menuItemDetails.prices[0] || 0,
        usesFractionalQuantity: data?.menuItemDetails?.usesFractionalQuantity,
        unitOfMeasure: data?.menuItemDetails?.unitOfMeasure,
        name: data?.menuItemDetails.name
      }
    }

    return !calledSelection || !selectionItemDetails
      ? getModifiersFromMenuQuery()
      : getModifiersFromSelectionQuery(selectionItemDetails)
  }, [
    calledSelection,
    selectionItemDetails,
    data,
    itemGroupGuid,
    modifierGroups,
    props.itemGuid
  ])
  const itemTags = useItemTags(
    props.itemTags,
    data?.menuItemDetails?.contentAdvisories
  )

  if (shouldSkip) {
    return null
  }

  return (
    <ModifierContextProvider
      modifier={rootModifier}
      rootRef={rootRef}
      modifierGroupData={modifierMap}
      modifierAllergenMap={modifierAllergenMap}
      quantity={
        selectionItemDetails?.fractionalQuantity?.quantity ||
        selectionItemDetails?.quantity ||
        1
      }
    >
      <div className='itemModalContainer'>
        <SitesToastContainer
          containerId='itemModalToastContainer'
          style={{ zIndex: 11 }}
        />
        <div className='itemModal'>
          <ModalBody
            name={data?.menuItemDetails.name || selectionItemDetails?.name}
            itemGuid={props.itemGuid}
            itemGroupGuid={props.itemGroupGuid}
            itemMenuGuid={props.itemMenuGuid}
            basePrice={
              hasSelection
                ? selectionItemDetails?.menuItemPrice
                : data?.menuItemDetails.prices[0] || 0
            }
            description={
              data?.menuItemDetails.description ||
              selectionItemDetails?.description
            }
            specialRequests={selectionItemDetails?.specialRequest}
            itemTags={itemTags}
            groups={
              !loading
                ? data?.menuItemDetails.modifierGroups ||
                  selectionItemDetails?.modifierGroups ||
                  []
                : null
            }
            image={
              data?.menuItemDetails?.imageUrls?.medium ||
              selectionItemDetails?.imageUrls?.medium
            }
            loading={loading}
            ref={rootRef}
            isReorder={hasReorderSelection}
            selectionGuid={props.selectionGuid}
            allergens={
              doMenuItemData?.doMenus_findMenuItem?.__typename ===
              'DoMenus_FindMenuItemResponse'
                ? doMenuItemData.doMenus_findMenuItem.menuItem.allergens
                : null
            }
            calories={data?.menuItemDetails?.calories}
            itemSource={props.itemSource}
            onSuccessfulAddToCart={props.onSuccessfulAddToCart}
            offer={props.offer}
          />
        </div>
        {loadingCartState && loadingCart && (
          <LoadingSpinnerOverlay withBorderRadius />
        )}
      </div>
    </ModifierContextProvider>
  )
}

const ItemModal = (props: ItemModalProps) => {
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      testId='menu-item-modal'
    >
      <ModalOverlay fadeIn={props.withTransitions} fadeOut />
      <ModalContent
        wrapperClassName='itemModalWrapper'
        contentClassName='itemModalContent modalContentV2 fullScreenMobile'
        ariaLabelledBy={headerId}
      >
        <WrappedItemModal key='modal' {...props} />
      </ModalContent>
    </Modal>
  )
}

export default ItemModal
